import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ChevronsUpDown, Check, ShieldCheck } from 'lucide-react'
import { api } from '../../api/client'
import { organizationBrowseQueries } from '../../api/organizationBrowseQueries'
import { useHierarchyLabels } from '../../hooks/useHierarchyLabels'
import type { MembershipResponse, MembershipEntityType } from '../../types'

interface ContextSwitcherProps {
  memberships: MembershipResponse[]
  currentMembershipId: string | undefined
  onSwitch: (membershipId: string) => void
  /** Set for a dual-hat admin: adds an entry that leaves the workspace for the admin console. */
  adminHref?: string
  platformHref?: string
  /** Where a membership on a level the tenant added is looked up: that level has no list of its own. */
  organizationId?: string | null
}

/**
 * A membership on one of the four shipped levels is named by its type. One on a
 * level the tenant added is named by its unit's level, which the type does not
 * know; until that is read, a neutral word rather than a guess.
 */
function levelLabel(
  entityType: MembershipEntityType,
  labelForLevel: (level: 'DIVISION' | 'VALUE_STREAM' | 'PRODUCT_TEAM' | 'ORGANIZATION') => string,
  t: (key: string) => string,
  unitLevelName?: string | null,
): string {
  return entityType === 'UNIT' ? unitLevelName || t('units.genericLevel') : labelForLevel(entityType)
}

/** A membership's name, and for a level the tenant added, that level's name too. */
function useEntityName(entityType: MembershipEntityType, entityId: string, organizationId?: string | null) {
  const { i18n } = useTranslation()
  const hasEntityId = !!entityId
  const { data: units } = useQuery({
    ...organizationBrowseQueries.units(organizationId ?? '', undefined, false, i18n.language),
    enabled: entityType === 'UNIT' && hasEntityId && !!organizationId,
  })
  const unit = entityType === 'UNIT' ? units?.find((candidate) => candidate.id === entityId) : undefined
  const { data: org } = useQuery({
    queryKey: ['organizations', 'detail', entityId],
    queryFn: () => api.organizations.get(entityId).then((r) => r.data),
    enabled: entityType === 'ORGANIZATION' && hasEntityId,
    staleTime: 1000 * 60 * 10,
  })
  const { data: div } = useQuery({
    queryKey: ['divisions', 'detail', entityId],
    queryFn: () => api.divisions.get(entityId).then((r) => r.data),
    enabled: entityType === 'DIVISION' && hasEntityId,
    staleTime: 1000 * 60 * 10,
  })
  const { data: vs } = useQuery({
    queryKey: ['valueStreams', 'detail', entityId],
    queryFn: () => api.valueStreams.get(entityId).then((r) => r.data),
    enabled: entityType === 'VALUE_STREAM' && hasEntityId,
    staleTime: 1000 * 60 * 10,
  })
  const { data: team } = useQuery({
    queryKey: ['productTeams', 'detail', entityId],
    queryFn: () => api.productTeams.get(entityId).then((r) => r.data),
    enabled: entityType === 'PRODUCT_TEAM' && hasEntityId,
    staleTime: 1000 * 60 * 10,
  })

  const name = (() => {
    switch (entityType) {
      case 'ORGANIZATION': return org?.name ?? null
      case 'DIVISION': return div?.name ?? null
      case 'VALUE_STREAM': return vs?.name ?? null
      case 'PRODUCT_TEAM': return team?.name ?? null
      case 'UNIT': return unit?.name ?? null
      default: return null
    }
  })()
  return { name, unitLevelName: unit?.levelName ?? null }
}

function MembershipItem({
  membership,
  isCurrent,
  onSelect,
  organizationId,
}: {
  membership: MembershipResponse
  isCurrent: boolean
  onSelect: () => void
  organizationId?: string | null
}) {
  const { t } = useTranslation()
  const { labelForLevel } = useHierarchyLabels()
  const { name: entityName, unitLevelName } = useEntityName(membership.entityType, membership.entityId, organizationId)

  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors ${
        isCurrent
          ? 'bg-primary/10 text-primary'
          : 'text-foreground hover:bg-accent'
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{entityName ?? '...'}</p>
        <p className="text-xs text-muted-foreground">
          {levelLabel(membership.entityType, labelForLevel, t, unitLevelName)} &middot; {membership.role}
        </p>
      </div>
      {isCurrent && <Check className="h-4 w-4 shrink-0 text-primary" />}
    </button>
  )
}

export function ContextSwitcher({ memberships, currentMembershipId, onSwitch, adminHref, platformHref, organizationId }: ContextSwitcherProps) {
  const { t } = useTranslation()
  const { labelForLevel } = useHierarchyLabels()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Find the current membership to show in the trigger
  const current = memberships.find((m) => m.id === currentMembershipId)
  const { name: currentName, unitLevelName: currentLevelName } = useEntityName(
    current?.entityType ?? 'ORGANIZATION',
    current?.entityId ?? '',
    organizationId,
  )

  // Close on click outside
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Escape closes the menu and hands focus back to the trigger; tabbing out of it closes it too.
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && open) {
      e.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
    }
  }

  function handleBlur(e: React.FocusEvent) {
    if (open && !e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
  }

  // Single membership — just show name, no dropdown
  if (memberships.length <= 1 && !adminHref && !platformHref) {
    return currentName ? (
      <p className="text-xs text-primary/70 truncate">{currentName}</p>
    ) : null
  }

  return (
    <div className="relative" ref={ref} onKeyDown={handleKeyDown} onBlur={handleBlur}>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        aria-haspopup="true"
        aria-expanded={open}
        className="flex items-center gap-1.5 text-xs text-primary/70 hover:text-primary transition-colors group max-w-full rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <span className="truncate">
          {currentName ?? t('common.loading')}
          {current && (
            <span className="text-muted-foreground ml-1">
              ({levelLabel(current.entityType, labelForLevel, t, currentLevelName)})
            </span>
          )}
        </span>
        <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50 group-hover:opacity-100" />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-64 rounded-xl border border-border bg-card shadow-lg z-50 p-1.5 animate-fade-in">
          <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
            {t('contextSwitcher.switchContext')}
          </p>
          <div className="max-h-60 overflow-y-auto">
            {memberships.map((m) => (
              <MembershipItem
                key={m.id}
                membership={m}
                isCurrent={m.id === currentMembershipId}
                organizationId={organizationId}
                onSelect={() => {
                  onSwitch(m.id)
                  setOpen(false)
                }}
              />
            ))}
          </div>
          {adminHref && (
            <div className="mt-1 border-t border-border pt-1">
              <Link
                to={adminHref}
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              >
                <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                {t('nav.administration')}
              </Link>
            </div>
          )}
          {platformHref && (
            <div className="mt-1 border-t border-border pt-1">
              <Link
                to={platformHref}
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              >
                <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                {t('nav.platformAdministration')}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
